import { MIGRATION_PATHS, DEFAULT_VERSIONS } from "./schemaRegistry";
import { validateEntity } from "./validationService";
import { logger, LOG_CATEGORIES } from "../../utils/logger";

/**
 * Migrate an entity to a target version
 * @param {Object} entity - Entity to migrate
 * @param {string} entityType - Type of entity
 * @param {string} targetVersion - Target version
 * @returns {Promise<Object>} Migrated entity
 */
export async function migrateEntity(entity, entityType, targetVersion) {
    try {
        const currentVersion = entity.version || "v1.0.0";
        
        // If already at target version, return as is
        if (currentVersion === targetVersion) {
            return entity;
        }

        await logger.info(LOG_CATEGORIES.SERIALIZATION, "Starting entity migration", {
            entityType,
            entityId: entity.id,
            fromVersion: currentVersion,
            toVersion: targetVersion
        });

        // Find migration path
        const migrationPath = findMigrationPath(entityType, currentVersion, targetVersion);
        
        if (!migrationPath) {
            throw new Error(`No migration path found from ${currentVersion} to ${targetVersion} for ${entityType}`);
        }

        // Apply migrations step by step
        let migratedEntity = { ...entity };
        
        for (const migrationStep of migrationPath) {
            migratedEntity = await applyMigration(migratedEntity, entityType, migrationStep);
            
            // Validate intermediate state
            await validateEntity(migratedEntity, entityType, migrationStep.toVersion);
        }

        // Update version
        migratedEntity.version = targetVersion;
        migratedEntity.migratedAt = new Date().toISOString();

        await logger.info(LOG_CATEGORIES.SERIALIZATION, "Entity migration completed", {
            entityType,
            entityId: entity.id,
            fromVersion: currentVersion,
            toVersion: targetVersion
        });

        return migratedEntity;

    } catch (error) {
        await logger.error(LOG_CATEGORIES.SERIALIZATION, "Entity migration failed:", error, {
            entityType,
            entityId: entity?.id,
            targetVersion
        });
        throw error;
    }
}

/**
 * Find migration path between two versions
 * @param {string} entityType - Entity type
 * @param {string} fromVersion - Source version
 * @param {string} toVersion - Target version
 * @returns {Array} Array of migration steps
 */
function findMigrationPath(entityType, fromVersion, toVersion) {
    const entityMigrations = MIGRATION_PATHS[entityType];
    
    if (!entityMigrations) {
        return null;
    }

    // Simple direct migration check
    if (entityMigrations[fromVersion] && entityMigrations[fromVersion][toVersion]) {
        return [{
            fromVersion,
            toVersion,
            migrationFn: entityMigrations[fromVersion][toVersion]
        }];
    }

    // For complex migrations, we could implement path finding algorithm
    // For now, return null if no direct path exists
    return null;
}

/**
 * Apply a single migration step
 * @param {Object} entity - Entity to migrate
 * @param {string} entityType - Entity type
 * @param {Object} migrationStep - Migration step definition
 * @returns {Promise<Object>} Migrated entity
 */
async function applyMigration(entity, entityType, migrationStep) {
    try {
        const { fromVersion, toVersion, migrationFn } = migrationStep;

        await logger.info(LOG_CATEGORIES.SERIALIZATION, "Applying migration step", {
            entityType,
            entityId: entity.id,
            fromVersion,
            toVersion
        });

        // Apply migration function
        const migratedEntity = migrationFn(entity);

        // Validate migrated entity
        await validateEntity(migratedEntity, entityType, toVersion);

        return migratedEntity;

    } catch (error) {
        await logger.error(LOG_CATEGORIES.SERIALIZATION, "Migration step failed:", error, {
            entityType,
            entityId: entity.id,
            migrationStep
        });
        throw error;
    }
}

/**
 * Check if migration is needed for an entity
 * @param {Object} entity - Entity to check
 * @param {string} entityType - Entity type
 * @returns {boolean} Whether migration is needed
 */
export function needsMigration(entity, entityType) {
    const currentVersion = entity.version || "v1.0.0";
    const targetVersion = DEFAULT_VERSIONS[entityType];
    
    return currentVersion !== targetVersion;
}

/**
 * Get migration path information
 * @param {string} entityType - Entity type
 * @param {string} fromVersion - Source version
 * @param {string} toVersion - Target version
 * @returns {Object|null} Migration path information
 */
export function getMigrationInfo(_entityType, _fromVersion, _toVersion) {
    const path = findMigrationPath(_entityType, _fromVersion, _toVersion);
    
    if (!path) {
        return null;
    }

    return {
        entityType: _entityType,
        fromVersion: _fromVersion,
        toVersion: _toVersion,
        steps: path.length,
        estimatedTime: path.length * 100, // Rough estimate in milliseconds
        breakingChanges: detectBreakingChanges(_entityType, _fromVersion, _toVersion)
    };
}

/**
 * Detect breaking changes between versions
 * @param {string} entityType - Entity type
 * @param {string} fromVersion - Source version
 * @param {string} toVersion - Target version
 * @returns {Array} Array of breaking changes
 */
function detectBreakingChanges(_entityType, _fromVersion, _toVersion) {
    const breakingChanges = [];

    // This would be implemented based on schema definitions
    // For now, return empty array
    return breakingChanges;
}

/**
 * Rollback entity to a previous version
 * @param {Object} entity - Entity to rollback
 * @param {string} entityType - Entity type
 * @param {string} targetVersion - Target version for rollback
 * @returns {Promise<Object>} Rollbacked entity
 */
export async function rollbackEntity(entity, entityType, targetVersion) {
    try {
        const currentVersion = entity.version;
        
        if (!currentVersion) {
            throw new Error("Entity has no version information for rollback");
        }

        await logger.warn(LOG_CATEGORIES.SERIALIZATION, "Rolling back entity", {
            entityType,
            entityId: entity.id,
            fromVersion: currentVersion,
            toVersion: targetVersion
        });

        // Find rollback path (reverse migration)
        const rollbackPath = findMigrationPath(entityType, targetVersion, currentVersion);
        
        if (!rollbackPath) {
            throw new Error(`No rollback path found from ${currentVersion} to ${targetVersion} for ${entityType}`);
        }

        // Apply rollback migrations in reverse order
        let rollbackedEntity = { ...entity };
        
        for (let i = rollbackPath.length - 1; i >= 0; i--) {
            const migrationStep = rollbackPath[i];
            rollbackedEntity = await applyRollback(rollbackedEntity, entityType, migrationStep);
        }

        // Update version
        rollbackedEntity.version = targetVersion;
        rollbackedEntity.rollbackedAt = new Date().toISOString();
        rollbackedEntity.rollbackedFrom = currentVersion;

        await logger.warn(LOG_CATEGORIES.SERIALIZATION, "Entity rollback completed", {
            entityType,
            entityId: entity.id,
            fromVersion: currentVersion,
            toVersion: targetVersion
        });

        return rollbackedEntity;

    } catch (error) {
        await logger.error(LOG_CATEGORIES.SERIALIZATION, "Entity rollback failed:", error, {
            entityType,
            entityId: entity?.id,
            targetVersion
        });
        throw error;
    }
}

/**
 * Apply a single rollback step
 * @param {Object} entity - Entity to rollback
 * @param {string} entityType - Entity type
 * @param {Object} migrationStep - Migration step to reverse
 * @returns {Promise<Object>} Rollbacked entity
 */
async function applyRollback(entity, entityType, migrationStep) {
    try {
        const { fromVersion, toVersion } = migrationStep;

        await logger.warn(LOG_CATEGORIES.SERIALIZATION, "Applying rollback step", {
            entityType,
            entityId: entity.id,
            fromVersion: toVersion, // Reverse direction
            toVersion: fromVersion
        });

        // For now, we'll use a simple approach
        // In a real implementation, you'd have specific rollback functions
        const rollbackedEntity = { ...entity };

        // Remove fields that were added in the migration
        switch (entityType) {
        case "User":
            if (toVersion === "v1.1.0" && fromVersion === "v1.0.0") {
                delete rollbackedEntity.kycStatus;
                delete rollbackedEntity.riskProfile;
            }
            break;
        case "Opportunity":
            if (toVersion === "v1.1.0" && fromVersion === "v1.0.0") {
                delete rollbackedEntity.approvedBy;
                delete rollbackedEntity.complianceStatus;
            }
            break;
        case "Investment":
            if (toVersion === "v1.1.0" && fromVersion === "v1.0.0") {
                delete rollbackedEntity.complianceCheck;
                delete rollbackedEntity.riskAssessment;
            }
            break;
        }

        return rollbackedEntity;

    } catch (error) {
        await logger.error(LOG_CATEGORIES.SERIALIZATION, "Rollback step failed:", error, {
            entityType,
            entityId: entity.id,
            migrationStep
        });
        throw error;
    }
}

/**
 * Get all available versions for an entity type
 * @param {string} entityType - Entity type
 * @returns {Array} Array of available versions
 */
export function getAvailableVersions(entityType) {
    const entityMigrations = MIGRATION_PATHS[entityType];
    
    if (!entityMigrations) {
        return [];
    }

    const versions = new Set();
    
    // Add all versions from migration paths
    Object.keys(entityMigrations).forEach(fromVersion => {
        versions.add(fromVersion);
        Object.keys(entityMigrations[fromVersion]).forEach(toVersion => {
            versions.add(toVersion);
        });
    });

    return Array.from(versions).sort();
} 